import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { use as _$use } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<video class=cctv-video controls muted autoplay>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=cctv-panel><div class=cctv-panel-header><span class=cctv-panel-title>CCTV: </span><button class=cctv-panel-close>✕</button></div><div class=cctv-panel-content>`),
  _tmpl$3 = /*#__PURE__*/_$template(`<img class=cctv-image>`);
/**
 * WorldView - CCTV Panel
 * Displays live video feed when a CCTV camera is selected.
 * Uses HLS.js for HLS streams; displays an <img> for image-only cameras.
 */

import { createEffect, createMemo, createSignal, onCleanup, Show, untrack } from "solid-js";
import Hls from "hls.js";
import { groundState, setCenterStageCamera } from "../../layers/ground/store";
import { PROXY_ENDPOINTS } from "../../config";
import { useCesium } from "../../cesium/useCesium";
/** HLS sources that require token-gated relay (CORS-blocked if fetched directly) */
const TOKEN_GATED_SOURCES = ["arkansas"];
export function CCTVPanel() {
  const {
    viewer,
    ready
  } = useCesium();
  let videoRef;
  let hlsInstance = null;
  const [isVideo, setIsVideo] = createSignal(false);
  const [imageSrc, setImageSrc] = createSignal("");

  // Memoize camera lookup to avoid repeated finds
  const selectedCamera = createMemo(() => {
    const id = groundState.centerStageCameraId;
    return id ? groundState.cctvCameras.find(c => c.id === id) : null;
  });
  function destroyHls() {
    if (hlsInstance) {
      hlsInstance.destroy();
      hlsInstance = null;
    }
  }
  function startHls(url) {
    if (!videoRef) return;
    if (Hls.isSupported()) {
      hlsInstance = new Hls({
        lowLatencyMode: true
      });
      hlsInstance.loadSource(url);
      hlsInstance.attachMedia(videoRef);
      hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => videoRef?.play().catch(console.error));
      hlsInstance.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) console.error("[CCTVPanel] HLS fatal error:", data);
      });
    } else if (videoRef.canPlayType("application/vnd.apple.mpegurl")) {
      videoRef.src = url;
      videoRef.play().catch(console.error);
    } else {
      console.warn("[CCTVPanel] HLS not supported");
    }
  }
  createEffect(() => {
    const cameraId = groundState.centerStageCameraId;
    destroyHls();
    setIsVideo(false);
    setImageSrc("");
    if (!cameraId) return;
    const camera = untrack(() => selectedCamera());
    if (!camera) return;
    const hlsMedia = camera.media.find(m => m.type === "hls");
    if (hlsMedia) {
      setIsVideo(true);
      const sourcePrefix = cameraId.split("-")[0] ?? "";
      const hlsUrl = TOKEN_GATED_SOURCES.includes(sourcePrefix) ? PROXY_ENDPOINTS.cctvHlsRelay(cameraId) : hlsMedia.url;
      // Defer HLS init to next microtask so the <video> element renders first
      queueMicrotask(() => startHls(hlsUrl));
    } else {
      setImageSrc(PROXY_ENDPOINTS.cctvThumbnail(cameraId));
    }
  });

  // Pan viewport to center on the selected camera (keep current altitude)
  createEffect(() => {
    if (!groundState.centerStageCameraId || !ready()) return;
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    const cam = untrack(() => selectedCamera());
    if (!cam) return;
    v.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(cam.longitude, cam.latitude, v.camera.positionCartographic.height),
      duration: 1.5
    });
  });
  onCleanup(destroyHls);
  return _$createComponent(Show, {
    get when() {
      return selectedCamera();
    },
    children: camera => (() => {
      var _el$ = _tmpl$2(),
        _el$2 = _el$.firstChild,
        _el$3 = _el$2.firstChild,
        _el$4 = _el$3.firstChild,
        _el$5 = _el$3.nextSibling,
        _el$6 = _el$2.nextSibling;
      _$insert(_el$3, () => camera().name, null);
      _el$5.$$click = () => setCenterStageCamera(null);
      _$insert(_el$6, _$createComponent(Show, {
        get when() {
          return isVideo();
        },
        get fallback() {
          return (() => {
            var _el$8 = _tmpl$3();
            _$effect(_p$ => {
              var _v$ = imageSrc(),
                _v$2 = camera().name;
              _v$ !== _p$.e && _$setAttribute(_el$8, "src", _p$.e = _v$);
              _v$2 !== _p$.t && _$setAttribute(_el$8, "alt", _p$.t = _v$2);
              return _p$;
            }, {
              e: undefined,
              t: undefined
            });
            return _el$8;
          })();
        },
        get children() {
          var _el$7 = _tmpl$();
          var _ref$ = videoRef;
          typeof _ref$ === "function" ? _$use(_ref$, _el$7) : videoRef = _el$7;
          _$effect(() => _$setAttribute(_el$7, "poster", PROXY_ENDPOINTS.cctvThumbnail(camera().id)));
          return _el$7;
        }
      }));
      return _el$;
    })()
  });
}
export default CCTVPanel;
_$delegateEvents(["click"]);